<script lang="ts" setup>
import { computed, ref } from 'vue'

import EventEditActionButtons from '@/pages/CreateEventProcessingPage/ui/layout/EventEditActionButtons.vue'
import { IEventEdit } from '@/enitites/event'
import {
  useAddGuestToEventMutation,
  useEventGuestsQuery,
  useRemoveGuestFromEventMutation,
} from '@/enitites/event/api/queries'
import { useImage } from '@/shared/composables/image'
import BKInput from '@/components/AppShared/BKInput.vue'
import { useUsersInfiniteQuery } from '@/enitites/user/api/queries'
import useValidationRules from '@/shared/composables/validation'
import AvatarPlaceholder from '@/features/tickets/assets/AvatarPlaceholder.vue'
import { UserDB } from '@/enitites/user'
import { IEventGuestPayload } from '@/enitites/event/model/types'
import { mdiMinusCircleOutline } from '@mdi/js'

const props = defineProps<{
  eventData: IEventEdit
}>()

const emit = defineEmits<{
  (e: 'next'): void
  (e: 'back'): void
  (e: 'cancel'): void
}>()

const errorMessage = ref<string>()

const searchKey = ref('')

const { getAvatar } = useImage()
const rules = useValidationRules()

const { data: guests, isLoading: isLoadingGuests, error: errorGuests } = useEventGuestsQuery(props.eventData.id)
const { data: searchResults, isLoading: isLoadingSearchResults } = useUsersInfiniteQuery(
  // @ts-expect-error ignore
  computed(() => {
    return {
      pageSize: 100,
      keyword: searchKey.value,
      exclude: guests.value ? guests.value.map((guest) => guest.assignedTo?.userId) : [],
    }
  })
)
const { mutate: addGuestMutation, isPending: isAddingGuest, error: errorAddingGuest } = useAddGuestToEventMutation()
const {
  mutate: removeGuestMutation,
  isPending: isRemovingGuest,
  error: errorRemovingGuest,
} = useRemoveGuestFromEventMutation()

const searchUsers = computed(() => {
  return searchResults.value ? searchResults.value.pages.flatMap((page) => page) : []
})
const selectedGuest = ref<UserDB | string | null>(null)
const fullNameForEmail = ref('')
const label = ref('')
const selectedTierId = ref<number | null>(null)
const isFormValid = ref(false)

const updateUserSelectorKey = ref(0)
const handleSelectedUserChange = () => {
  updateUserSelectorKey.value += 1
}

const validate = (value: unknown) => {
  if (typeof value === 'string') {
    return rules.email(value)
  }
  if (value && typeof value === 'object' && ('id' in value || 'userId' in value)) {
    return true
  }
  return 'Please select a valid user or enter a valid email address.'
}

const handleAddGuest = async () => {
  if (!selectedGuest.value || !selectedTierId.value || isAddingGuest.value) return

  const guestData: IEventGuestPayload =
    typeof selectedGuest.value === 'string'
      ? {
          tierId: selectedTierId.value,
          label: label.value,
          email: selectedGuest.value,
          name: fullNameForEmail.value,
        }
      : {
          userId: selectedGuest.value.id,
          tierId: selectedTierId.value,
          label: label.value,
        }

  addGuestMutation(
    {
      eventId: props.eventData.id,
      data: guestData,
    },
    {
      onSuccess() {
        selectedGuest.value = null
        fullNameForEmail.value = ''
        label.value = ''
        selectedTierId.value = null
        isFormValid.value = false
      },
    }
  )
}

const handleRemoveGuest = async (guestId: number) => {
  if (isRemovingGuest.value) return

  removeGuestMutation({
    eventId: props.eventData.id,
    guestId,
  })
}

function handleNext() {
  emit('next')
}

function handleCancel() {
  emit('cancel')
}

const getAgeByBirthdate = (birthdate: string): number => {
  const today = new Date()
  const birthDate = new Date(birthdate)
  let age = today.getFullYear() - birthDate.getFullYear()
  const monthDifference = today.getMonth() - birthDate.getMonth()
  if (monthDifference < 0 || (monthDifference === 0 && today.getDate() < birthDate.getDate())) {
    age -= 1
  }
  return age
}
</script>

<template>
  <div class="event-guest-list pb-10">
    <div class="title-container">
      <p class="form-title">Guest List</p>
      <p class="form-subtitle">This is the list of attendees who will have their ticket fees waived.</p>
    </div>

    <div v-if="isLoadingGuests">Loading guests...</div>

    <div v-if="errorGuests">Error loading guests: {{ errorGuests.message }}</div>

    <div class="guests-list">
      <div
        v-for="guest in guests"
        :key="guest.id"
        class="guest-item"
      >
        <div class="guest-avatar-name">
          <v-avatar
            class="guest-avatar"
            left
          >
            <img
              :alt="guest.assignedTo?.dancerName"
              :src="getAvatar(guest.assignedTo?.avatar || '', 100)"
            />
          </v-avatar>
          <div class="guest-info">
            <span class="guest-name">
              {{ guest.assignedTo?.dancerName }}
            </span>
            <span class="guest-added-at"> Added: {{ new Date(guest.addedAt).toLocaleDateString() }} </span>
            <span
              v-if="guest.addedBy?.dancerName"
              class="guest-added-by"
            >
              By: {{ guest.addedBy.dancerName }}
            </span>
          </div>
        </div>
        <span class="guest-role">
          {{ guest.label }}
        </span>
        <div class="guest-action">
          <v-btn
            icon
            color="var(--error-red)"
            :disabled="isRemovingGuest"
            @click="handleRemoveGuest(guest.id)"
          >
            <v-icon>
              {{ mdiMinusCircleOutline }}
            </v-icon>
          </v-btn>
        </div>
      </div>

      <v-divider class="divider" />

      <div class="add-guest-section">
        <p class="form-title">Add Guest</p>

        <div class="select-wrapper">
          <v-combobox
            :key="updateUserSelectorKey"
            v-model="selectedGuest"
            append-icon=""
            class="user-selector"
            color="var(--color-dark-800)"
            eager
            height="56"
            hide-details="auto"
            :items="searchUsers"
            item-color="var(--color-dark-800)"
            item-value="id"
            item-text="dancerName"
            :loading="isLoadingSearchResults"
            :menu-props="{
              attach: '.select-wrapper',
              contentClass: 'user-select-menu',
              eager: true,
              maxHeight: 279,
            }"
            no-filter
            outlined
            placeholder="Search users by name or email"
            return-object
            :rules="[validate]"
            :search-input.sync="searchKey"
            validate-on-blur
            @focus="selectedGuest = null"
            @change="handleSelectedUserChange"
          >
            <template #selection="{ item }">
              <div
                v-if="typeof item === 'object' && item.dancerName"
                class="selected-item"
              >
                <AvatarPlaceholder
                  v-if="!item.avatar"
                  background-color="#DDDDDD"
                  color="#333333"
                  size="24"
                />
                <v-avatar
                  v-else
                  size="24"
                >
                  <v-img :src="getAvatar(item.avatar, 48)" />
                </v-avatar>
                <span>
                  {{ item.dancerName }}
                </span>
              </div>
              <span
                v-else
                class="selected-item"
              >
                <AvatarPlaceholder
                  background-color="#DDDDDD"
                  color="#333333"
                  size="24"
                />
                {{ typeof item === 'string' ? item : item.email ? item.email : 'Unassigned' }}
              </span>
            </template>
            <template #no-data>
              <div class="no-data-item">
                <v-progress-circular
                  v-if="isLoadingSearchResults"
                  indeterminate
                  color="var(--color-blue-800)"
                />
                <span v-else>
                  <b>No users found</b><br />
                  If the user doesn’t have a profile, use their email to send and assign ticket.
                </span>
              </div>
            </template>
            <template #item="{ item }">
              <div class="select-item">
                <AvatarPlaceholder
                  v-if="!item.avatar"
                  background-color="#DDDDDD"
                  color="#333333"
                />
                <v-avatar
                  v-else
                  size="20"
                >
                  <v-img :src="getAvatar(item.avatar, 48)" />
                </v-avatar>
                <span> {{ item.dancerName }} </span>
              </div>
            </template>
          </v-combobox>
        </div>

        <v-form
          v-if="selectedGuest"
          v-model="isFormValid"
          class="additional-fields"
        >
          <div
            v-if="typeof selectedGuest === 'object'"
            class="selected-user-card"
          >
            <div class="selected-user">
              <v-avatar size="100">
                <v-img :src="getAvatar(selectedGuest?.avatar || '', 200)" />
              </v-avatar>

              <div class="selected-user-info">
                <p class="selected-user-dancer-name">
                  {{ selectedGuest.dancerName }}
                </p>
                <p class="selected-user-full-name">
                  {{ selectedGuest.fullName
                  }}{{ selectedGuest.birthday ? `, ${getAgeByBirthdate(selectedGuest.birthday)}` : '' }}
                </p>
                <p class="selected-user-location">
                  {{ selectedGuest.city }}{{ selectedGuest.state ? ` ${selectedGuest.state}` : ''
                  }}{{ selectedGuest.country ? `, ${selectedGuest.country}` : '' }}
                </p>
              </div>
            </div>
          </div>

          <div
            v-if="typeof selectedGuest === 'string'"
            class="selected-user-email"
          >
            <div class="confirmation-message">
              <b>Can’t find a profile for this email.</b> We’ll use email to assign and send ticket.
            </div>

            <BKInput
              v-model="fullNameForEmail"
              placeholder="Add recipient’s full name (optional)"
            />
          </div>

          <BKInput
            v-model="label"
            label="Guest Label"
            placeholder="e.g., VIP Guest, Speaker"
          />

          <v-select
            v-if="selectedGuest"
            v-model="selectedTierId"
            hide-details="auto"
            :items="eventData.tiers || []"
            item-text="tierTitle"
            item-value="id"
            item-color="var(--color-blue-800)"
            label="Select Ticket Tier"
            color="var(--color-blue-800)"
            outlined
            :rules="[rules.required]"
          />

          <BKButton
            :disabled="!isFormValid"
            white-text
            @click="handleAddGuest"
          >
            Add Guest
          </BKButton>
        </v-form>
      </div>
    </div>

    <EventEditActionButtons
      :is-loading="false"
      :disabled="false"
      :error="errorMessage"
      @next="handleNext"
      @back="$emit('back')"
      @cancel="handleCancel"
    />

    <v-snackbar
      :value="errorAddingGuest?.message || errorRemovingGuest?.message"
      color="var(--error-red)"
    >
      {{ errorAddingGuest?.message || errorRemovingGuest?.message }}
    </v-snackbar>
  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/style/mixins';

.event-guest-list {
  max-width: 500px;
  margin: 140px auto 60px;
  padding: 20px;

  .title-container {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
    margin-bottom: 12px;
  }

  .form-title {
    margin-bottom: 0;
    color: var(--color-dark-800);
    font-weight: 700;
    font-size: 16px;
    line-height: 16px;
  }

  .form-subtitle {
    margin-bottom: 0;
    color: var(--color-dark-800);
    font-size: 16px;
    line-height: 16px;
  }

  .guests-list {
    margin-bottom: 24px;

    .guest-item {
      display: flex;
      align-items: center;
      gap: 16px;
      padding: 12px 0;

      &:last-child {
        border-bottom: none;
      }

      .guest-avatar-name {
        display: flex;
        align-items: center;
        gap: 12px;
        flex: 1;
        min-width: 0;

        .guest-info {
          display: flex;
          flex-direction: column;
          gap: 2px;

          .guest-name {
            font-weight: 500;
            color: var(--color-dark-800);
          }

          .guest-added-at,
          .guest-added-by {
            font-size: 12px;
            color: var(--color-grey-300);
            line-height: 1.2;
          }
        }
      }

      .guest-role {
        font-size: 16px;
        color: var(--color-dark-800);
      }

      .guest-action {
        display: flex;
        align-items: center;
        justify-content: center;
        min-width: 48px;
      }
    }
  }

  .select-wrapper {
    position: relative;

    .selected-item {
      font-size: 16px;
      line-height: 24px;
      color: var(--color-dark-800);
      display: flex;
      align-items: center;
      gap: 4px;

      .hint-mark {
        color: var(--color-grey-300);
      }
    }

    .user-selector {
      border-radius: 6px;
      border-color: var(--color-grey-300);

      :deep(.v-input__slot) {
        background-color: var(--color-white);
      }

      &.v-select--is-menu-active {
        border-radius: 6px 6px 0 0;
        border: 1px solid var(--color-grey-300) !important;

        :deep(*) {
          border: none !important;
        }

        .selected-item {
          display: none;
        }
      }
    }

    .select-item {
      display: flex;
      align-items: center;
      gap: 4px;
      font-size: 16px;
      line-height: 24px;
      color: var(--color-dark-800);
    }

    .no-data-item {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 12px 24px;
      font-size: 16px;
      line-height: 24px;
      color: var(--color-dark-800);
    }
  }

  .divider {
    margin: 16px 0;
  }

  .add-guest-section,
  .additional-fields {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .add-guest-section {
    padding-bottom: 16px;
  }

  .selected-user-card {
    display: flex;
    flex-direction: column;
    align-items: center;

    .selected-user {
      display: flex;
      align-items: center;
      gap: 16px;
      padding: 20px;

      .selected-user-info {
        display: flex;
        flex-direction: column;
        gap: 4px;

        .selected-user-dancer-name {
          font-weight: 500;
          font-size: 20px;
          line-height: 1.3;
          color: var(--color-dark-800);
          margin: 0;
        }

        .selected-user-full-name,
        .selected-user-location {
          font-size: 14px;
          line-height: 20px;
          color: var(--color-grey-300);
          margin: 0;
        }
      }
    }

    .confirmation-message {
      max-width: 220px;
      font-size: 14px;
      line-height: 20px;
      color: var(--color-grey-300);
      text-align: center;
      text-wrap: balance;
    }
  }

  .selected-user-email {
    display: flex;
    flex-direction: column;
    gap: 12px;

    .confirmation-message {
      font-size: 12px;
      line-height: 14px;
      color: var(--color-dark-800);
    }
  }
}

:deep(.user-select-menu) {
  position: static;
  border-radius: 0 0 6px 6px;
  border: 1px solid var(--color-grey-300);
  border-top: none;
  box-shadow: none;
  min-width: 100% !important;

  .v-list-item {
    padding: 0 12px;
  }
}
</style>
